import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  alpha,
} from '@mui/material';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
} from '../../theme/harmonateStyles';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import CollectionTierInfo from './CollectionTierInfo'; // Import your CollectionTierInfo component

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DebtCollection = () => {
  const [currentView, setCurrentView] = useState('main');
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Sample data for debt collection files
  const debtFiles = [
    { id: 1, dueDate: '2/23/25', amount: 25000, probabilityScore: 'Medium', probabilityPercentage: 66 },
    { id: 2, dueDate: '2/25/25', amount: 15000, probabilityScore: 'Low', probabilityPercentage: 35 },
    { id: 3, dueDate: '2/11/25', amount: 5000, probabilityScore: 'High', probabilityPercentage: 88 },
    { id: 4, dueDate: '4/1/25', amount: 5500, probabilityScore: 'High', probabilityPercentage: 94 },
    { id: 5, dueDate: '5/25/25', amount: 14000, probabilityScore: 'High', probabilityPercentage: 87 },
    { id: 6, dueDate: '1/4/25', amount: 10000, probabilityScore: 'Medium', probabilityPercentage: 56 },
    { id: 7, dueDate: '11/21/24', amount: 2500, probabilityScore: 'Low', probabilityPercentage: 32 },
    { id: 8, dueDate: '2/14/25', amount: 12000, probabilityScore: 'Medium', probabilityPercentage: 51 },
    { id: 9, dueDate: '6/5/25', amount: 17250, probabilityScore: 'High', probabilityPercentage: 99 }
  ];

  // To Do List items
  const todoList = [
    'Vendor 7: Contact Lawyer',
    'Vendor 3: Respond to Email',
    'Vendor 6: Send Final Notice'
  ];

  // Sample data for collection trend chart
  const collectionTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Collected',
        data: [10, 20, 30, 25, 40, 50, 35, 45, 55, 40, 45, 50],
        borderColor: ht.green,
        backgroundColor: alpha(ht.green, 0.5),
        fill: true,
        tension: 0.4
      },
      {
        label: 'Outstanding',
        data: [30, 45, 25, 35, 20, 15, 25, 20, 35, 45, 25, 20],
        borderColor: ht.blue,
        backgroundColor: alpha(ht.blue, 0.5),
        fill: true,
        tension: 0.4
      }
    ]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 8,
          padding: 3,
          font: {
            size: 9
          }
        }
      },
      title: {
        display: true,
        text: '% of Debts Collected',
        position: 'top',
        font: {
          size: 11
        },
        padding: {
          top: 5,
          bottom: 5
        }
      },
      tooltip: {
        enabled: true,
        titleFont: {
          size: 10
        },
        bodyFont: {
          size: 10
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 9
          },
          padding: 2
        },
        grid: {
          display: true,
          drawBorder: true
        }
      },
      x: {
        ticks: {
          font: {
            size: 9
          },
          padding: 2
        },
        grid: {
          display: true,
          drawBorder: true
        }
      }
    },
    layout: {
      padding: {
        left: 2,
        right: 2,
        top: 2,
        bottom: 0
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'High':
        return <CheckCircleIcon sx={{ color: ht.green, fontSize: 16 }} />;
      case 'Medium':
        return <WarningIcon sx={{ color: '#E6A817', fontSize: 16 }} />;
      case 'Low':
        return <ErrorIcon sx={{ color: ht.red, fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const getRowBackgroundColor = (index) => {
    return index % 2 === 1 ? ht.listBg : ht.white;
  };

  const handleRowDoubleClick = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentView('details');
  };

  const handleBackToMain = () => {
    setCurrentView('main');
    setSelectedVendor(null);
  };

  // If we're in the details view, show the CollectionTierInfo component
  if (currentView === 'details') {
    return <CollectionTierInfo vendorData={selectedVendor} onBack={handleBackToMain} />;
  }

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Typography sx={{ ...harmonateDashboardTitleSx, mb: 2 }}>Debt Collection Files</Typography>
      
      <Grid container spacing={1.5}>
        {/* Debt Collection Files Table */}
        <Grid item xs={12}>
          <TableContainer component={Paper} elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, overflow: 'hidden' }}>
            <Table size="small" padding="none">
              <TableHead sx={{ bgcolor: ht.white }}>
                <TableRow>
                  <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Vendor</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Due Date</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Amount</TableCell>
                  <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, fontWeight: 700, color: ht.text, borderBottom: `1px solid ${ht.line}` }}>Probability Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {debtFiles.map((row, index) => (
                  <TableRow 
                    key={row.id} 
                    sx={{ 
                      bgcolor: getRowBackgroundColor(index),
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: ht.secondaryTab,
                      },
                      '& td': {
                        borderBottom: `1px solid ${ht.line}`,
                        color: ht.text,
                      },
                    }}
                    onDoubleClick={() => handleRowDoubleClick(row)}
                    title="Double-click to view details"
                  >
                    <TableCell sx={{ py: 0.25, px: 1, fontSize: 12 }}>{row.id}</TableCell>
                    <TableCell sx={{ py: 0.25, px: 1, fontSize: 12 }}>{row.dueDate}</TableCell>
                    <TableCell sx={{ py: 0.25, px: 1, fontSize: 12 }}>${row.amount.toLocaleString()}</TableCell>
                    <TableCell sx={{ py: 0.25, px: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon(row.probabilityScore)}
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: 12 }}>{row.probabilityScore}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5, fontSize: 12 }}>
                          {row.probabilityPercentage}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Collection Map Section */}
        <Grid item xs={12}>
          <Typography sx={{ ...harmonateDashboardTitleSx, mt: 0.75, mb: 0.75, fontSize: '1rem' }}>Collection Map</Typography>
        </Grid>

        {/* Chart and To Do List side by side */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 0.75, bgcolor: ht.white }}>
            <Box sx={{ height: 180 }}>
              <Line data={collectionTrendData} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4} lg={3}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 0.75, height: '100%', bgcolor: ht.white }}>
            <Typography variant="subtitle1" sx={{ mb: 0.5, fontSize: 14, fontWeight: 700, color: ht.text }}>To Do List</Typography>
            <List disablePadding dense>
              {todoList.map((item, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText 
                    primary={item} 
                    primaryTypographyProps={{ fontSize: 12 }}
                    sx={{ my: 0 }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Button 
          variant="contained" 
          size="small"
          sx={harmonateProceedButtonSx}
        >
          Export
        </Button>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          bgcolor: ht.white,
          borderRadius: '20px',
          boxShadow: ht.cardShadow,
          border: `1px solid ${ht.line}`,
          px: 1.25,
          py: 0.25
        }}>
          <Typography variant="subtitle2" sx={{ mr: 0.5, fontSize: 12, color: ht.text }}>Q2 2025</Typography>
          <KeyboardArrowUpIcon sx={{ fontSize: 16, color: ht.text }} />
        </Box>
      </Box>
    </Box>
  );
};

export default DebtCollection;